/**
 * Hanlyn Quick Shop Drawer
 *
 * Extends Dawn's quick shop modal functionality by presenting it as a bottom drawer.
 * Does not replace Dawn's add-to-cart, variant selection, or form submission logic.
 * Only changes the presentation layer from modal to drawer.
 */

if (!customElements.get('hanlyn-quick-shop-drawer')) {
  class HanlynQuickShopDrawer extends HTMLElement {
    constructor() {
      super();

      this.drawer = null;
      this.overlay = null;
      this.opener = null;
      this.focusTrap = null;
      this.lastFocusedElement = null;
      this.isOpen = false;
      this.cartUpdateUnsubscriber = null;

      // Touch/swipe support
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.touchStartY = 0;
      this.touchEndY = 0;

      // Bound methods for event listeners (so we can remove them)
      this.handleOverlayClick = this.handleOverlayClick.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleEscKey = this.handleEscKey.bind(this);
      this.handleFocusTrap = this.handleFocusTrap.bind(this);
      this.handleThumbnailClick = this.handleThumbnailClick.bind(this);
      this.handleArrowClick = this.handleArrowClick.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
      this.handleZoomClick = this.handleZoomClick.bind(this);
    }

    connectedCallback() {
      this.drawer = this.querySelector('.hanlyn-qs__drawer');

      if (!this.drawer) {
        console.error('HanlynQuickShopDrawer: .hanlyn-qs__drawer element not found');
        return;
      }

      // Find or create overlay
      this.overlay = document.querySelector('.hanlyn-qs__overlay');
      if (!this.overlay) {
        this.overlay = document.createElement('div');
        this.overlay.className = 'hanlyn-qs__overlay';
        document.body.appendChild(this.overlay);
      }

      // Set up event listeners
      this.setupEventListeners();

      // Subscribe to cart update events (Dawn's pubsub system)
      // When an item is added to cart, we close the quick shop drawer
      // and let Dawn's cart drawer open automatically
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
      }
    }

    disconnectedCallback() {
      // Clean up all event listeners
      this.removeEventListeners();

      // Remove overlay if it exists
      if (this.overlay && this.overlay.parentNode) {
        this.overlay.parentNode.removeChild(this.overlay);
      }

      // Unlock scroll if drawer was open
      if (this.isOpen) {
        this.unlockScroll();
      }

      // Unsubscribe from cart updates
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
    }

    setupEventListeners() {
      // Close button
      const closeButton = this.drawer.querySelector('.hanlyn-qs__close');
      if (closeButton) {
        closeButton.addEventListener('click', this.handleCloseClick);
      }

      // Overlay click
      if (this.overlay) {
        this.overlay.addEventListener('click', this.handleOverlayClick);
      }

      // ESC key
      document.addEventListener('keydown', this.handleEscKey);

      // Thumbnail clicks
      const thumbnails = this.drawer.querySelectorAll('.hanlyn-qs__thumb');
      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', this.handleThumbnailClick);
      });

      // Arrow navigation
      const prevButton = this.drawer.querySelector('.hanlyn-qs__arrow--prev');
      const nextButton = this.drawer.querySelector('.hanlyn-qs__arrow--next');
      if (prevButton) prevButton.addEventListener('click', this.handleArrowClick);
      if (nextButton) nextButton.addEventListener('click', this.handleArrowClick);

      // Zoom button
      const zoomButton = this.drawer.querySelector('.hanlyn-qs__zoom');
      if (zoomButton) zoomButton.addEventListener('click', this.handleZoomClick);
    }

    removeEventListeners() {
      const closeButton = this.drawer?.querySelector('.hanlyn-qs__close');
      if (closeButton) {
        closeButton.removeEventListener('click', this.handleCloseClick);
      }

      if (this.overlay) {
        this.overlay.removeEventListener('click', this.handleOverlayClick);
      }

      document.removeEventListener('keydown', this.handleEscKey);

      const thumbnails = this.drawer?.querySelectorAll('.hanlyn-qs__thumb');
      thumbnails?.forEach(thumb => {
        thumb.removeEventListener('click', this.handleThumbnailClick);
      });

      const prevButton = this.drawer?.querySelector('.hanlyn-qs__arrow--prev');
      const nextButton = this.drawer?.querySelector('.hanlyn-qs__arrow--next');
      if (prevButton) prevButton.removeEventListener('click', this.handleArrowClick);
      if (nextButton) nextButton.removeEventListener('click', this.handleArrowClick);

      const zoomButton = this.drawer?.querySelector('.hanlyn-qs__zoom');
      if (zoomButton) zoomButton.removeEventListener('click', this.handleZoomClick);

      const mainImageWrapper = this.drawer?.querySelector('.hanlyn-qs__main-image-wrapper');
      if (mainImageWrapper) {
        mainImageWrapper.removeEventListener('touchstart', this.handleTouchStart);
        mainImageWrapper.removeEventListener('touchend', this.handleTouchEnd);
      }
    }

    /**
     * Open the drawer with product content
     * @param {HTMLElement} openerElement - The button that triggered the drawer
     * @param {string} productUrl - URL to fetch product data from
     */
    async open(openerElement, productUrl) {
      if (this.isOpen) return;

      this.opener = openerElement;
      this.lastFocusedElement = document.activeElement;
      this.currentProductUrl = productUrl;

      try {
        // Fetch product data (mirroring Dawn's quick-add.js pattern)
        const response = await fetch(productUrl);
        const responseText = await response.text();
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');

        // Get the product-info element from the fetched page
        const productElement = responseHTML.querySelector('product-info');

        if (!productElement) {
          console.error('HanlynQuickShopDrawer: product-info element not found in fetched page');
          return;
        }

        // Populate drawer content
        await this.populateDrawer(productElement, responseHTML, productUrl);

        // Show drawer with animation
        this.showDrawer();

      } catch (error) {
        console.error('HanlynQuickShopDrawer: Error fetching product data', error);
      }
    }

    /**
     * Populate drawer with product data
     * @param {HTMLElement} productElement - The product-info element from Dawn
     * @param {Document} fullDocument - The full product page document
     * @param {string} productUrl - The product page URL
     */
    async populateDrawer(productElement, fullDocument, productUrl) {
      // Get product data
      const productTitleElement = fullDocument.querySelector('.product__title');

      // Extract ONLY the direct text content, not nested duplicates
      // The product page may have nested elements with duplicate title text
      let productTitle = '';

      if (productTitleElement) {
        // Try to get just the first text node or innerText to avoid nested duplicates
        // First, let's try getting the first child node that's actually text
        for (let node of productTitleElement.childNodes) {
          if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
            productTitle = node.textContent.trim();
            break;
          } else if (node.nodeType === Node.ELEMENT_NODE && node.textContent.trim()) {
            // If it's an element, get its text (but only the first one)
            productTitle = node.textContent.trim();
            break;
          }
        }

        // Fallback: if no child nodes had text, use the full textContent but clean duplicates
        if (!productTitle) {
          productTitle = productTitleElement.textContent.trim();
        }
      }

      const productPrice = productElement.querySelector('.price');
      const productMedia = fullDocument.querySelectorAll('.product__media.media img');
      const variantSelectors = productElement.querySelector('variant-selects, variant-radios');

      // Populate header
      const titleElement = this.drawer.querySelector('h2.hanlyn-qs__title#hanlyn-qs-title');
      if (titleElement) {
        titleElement.textContent = productTitle;
      }

      // Populate price
      const priceContainer = this.drawer.querySelector('.hanlyn-qs__price');
      if (priceContainer && productPrice) {
        // Clone price to manipulate it
        const clonedPrice = productPrice.cloneNode(true);

        // Remove any titles from price HTML
        const priceTitles = clonedPrice.querySelectorAll('.product__title, .product-title, h1, h2, h3, h4, h5, h6');
        priceTitles.forEach(title => title.remove());

        priceContainer.innerHTML = clonedPrice.outerHTML;
      }

      // Update "See all details" link
      const detailsLink = this.drawer.querySelector('.hanlyn-qs__details-link');
      if (detailsLink && productUrl) {
        detailsLink.href = productUrl;
      }

      // Populate media gallery
      this.populateMediaGallery(productMedia);

      // Clone and insert variant selectors
      if (variantSelectors) {
        const variantContainer = this.drawer.querySelector('.hanlyn-qs__variants');
        if (variantContainer) {
          // Clear existing content
          variantContainer.innerHTML = '';

          // Clone the variant selector (this preserves Dawn's JS functionality)
          const clonedVariants = variantSelectors.cloneNode(true);

          // Remove any titles from cloned variants BEFORE appending
          const variantTitles = clonedVariants.querySelectorAll('.product__title, .product-title, h1, h2, h3, h4, h5, h6');
          variantTitles.forEach(title => title.remove());

          variantContainer.appendChild(clonedVariants);

          // Transform variant presentation to match drawer design
          this.transformVariantDisplay(variantContainer, fullDocument);
        }
      }

      // Clone and insert the entire product-form element (to preserve Dawn's AJAX add-to-cart)
      const productFormElement = fullDocument.querySelector('product-form');
      if (productFormElement) {
        const formContainer = this.drawer.querySelector('.hanlyn-qs__form-wrapper');
        if (formContainer) {
          formContainer.innerHTML = '';
          const clonedProductForm = productFormElement.cloneNode(true);

          // Remove any titles from cloned form BEFORE appending
          const formTitles = clonedProductForm.querySelectorAll('.product__title, .product-title, h1, h2, h3, h4, h5, h6');
          formTitles.forEach(title => title.remove());

          formContainer.appendChild(clonedProductForm);

          // Style the submit button to match drawer design
          const submitButton = clonedProductForm.querySelector('[type="submit"]');
          if (submitButton) {
            submitButton.className = 'hanlyn-qs__add-to-cart';
          }

          // Ensure the cloned product-form connects to cart-drawer
          // The custom element will auto-initialize via connectedCallback
          // and find cart-drawer in the DOM
        }
      }

      // Set up Loox reviews injection point (if Loox is installed)
      this.setupLooxReviews(fullDocument);

      // Remove any stray title elements that might have been cloned
      requestAnimationFrame(() => {
        const headerTitle = this.drawer.querySelector('h2.hanlyn-qs__title#hanlyn-qs-title');
        const strayTitles = this.drawer.querySelectorAll('.product__title, .product-title');

        strayTitles.forEach(title => {
          if (title !== headerTitle) {
            title.remove();
          }
        });
      });
    }

    /**
     * Populate the media gallery with thumbnails and main image
     */
    populateMediaGallery(mediaElements) {
      const thumbnailsContainer = this.drawer.querySelector('.hanlyn-qs__thumbnails');
      const mainImageContainer = this.drawer.querySelector('.hanlyn-qs__main-image');

      if (!thumbnailsContainer || !mainImageContainer || !mediaElements || mediaElements.length === 0) {
        return;
      }

      // Clear existing thumbnails
      thumbnailsContainer.innerHTML = '';

      // Create thumbnails
      mediaElements.forEach((img, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'hanlyn-qs__thumb';
        button.setAttribute('data-media-index', index);
        button.setAttribute('aria-label', `View image ${index + 1}`);

        if (index === 0) {
          button.setAttribute('aria-current', 'true');
        }

        const thumbImg = document.createElement('img');
        thumbImg.src = img.src;
        thumbImg.alt = img.alt || '';
        thumbImg.loading = 'lazy';

        button.appendChild(thumbImg);
        thumbnailsContainer.appendChild(button);

        button.addEventListener('click', this.handleThumbnailClick);
      });

      // Set initial main image
      if (mediaElements.length > 0) {
        this.updateMainImage(0, mediaElements);
      }

      // Store media elements for later use
      this.mediaElements = Array.from(mediaElements);

      // Set up swipe/touch support for mobile
      const mainImageWrapper = this.drawer.querySelector('.hanlyn-qs__main-image-wrapper');
      if (mainImageWrapper) {
        mainImageWrapper.addEventListener('touchstart', this.handleTouchStart, { passive: true });
        mainImageWrapper.addEventListener('touchend', this.handleTouchEnd, { passive: true });
      }
    }

    /**
     * Update the main image display
     */
    updateMainImage(index, mediaElements = this.mediaElements) {
      if (!mediaElements || index >= mediaElements.length) return;

      const mainImageContainer = this.drawer.querySelector('.hanlyn-qs__main-image');
      if (!mainImageContainer) return;

      const img = mediaElements[index];
      mainImageContainer.innerHTML = `
        <img src="${img.src}" alt="${img.alt || ''}" loading="eager" />
      `;

      // Update active thumbnail
      const thumbnails = this.drawer.querySelectorAll('.hanlyn-qs__thumb');
      thumbnails.forEach((thumb, i) => {
        if (i === index) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      // Update arrow button states
      this.updateArrowStates(index, mediaElements.length);
    }

    /**
     * Update arrow button states based on current image
     * For endless carousel, arrows are never disabled
     */
    updateArrowStates(currentIndex, totalImages) {
      const prevButton = this.drawer.querySelector('.hanlyn-qs__arrow--prev');
      const nextButton = this.drawer.querySelector('.hanlyn-qs__arrow--next');

      // Arrows are always enabled for endless cycling
      if (prevButton) {
        prevButton.disabled = false;
      }

      if (nextButton) {
        nextButton.disabled = false;
      }
    }

    /**
     * Transform Dawn's variant display to match drawer design
     */
    transformVariantDisplay(container, fullDocument) {
      // This method transforms Dawn's default variant selectors into the drawer's design
      // Colors → image swatches, Sizes → pill buttons

      const fieldsets = container.querySelectorAll('fieldset');

      fieldsets.forEach(fieldset => {
        const legend = fieldset.querySelector('legend');
        const optionName = legend?.textContent?.trim()?.toLowerCase() || '';

        if (optionName.includes('color') || optionName.includes('colour')) {
          // Transform to color swatches
          fieldset.classList.add('hanlyn-qs__variant-group', 'hanlyn-qs__variant-group--colors');

          const inputs = fieldset.querySelectorAll('input[type="radio"]');
          inputs.forEach(input => {
            const label = fieldset.querySelector(`label[for="${input.id}"]`);
            if (label) {
              label.classList.add('hanlyn-qs__color-swatch');

              // Get color value
              const colorValue = input.value;
              label.setAttribute('data-color', colorValue);

              // Set pressed state if checked
              if (input.checked) {
                label.setAttribute('aria-pressed', 'true');
              } else {
                label.setAttribute('aria-pressed', 'false');
              }

              // Try to find variant image in the full document
              // Shopify typically has variant images with data-option attributes
              const variantImage = fullDocument.querySelector(`[data-variant-image][data-variant*="${colorValue}"]`);

              if (variantImage && variantImage.src) {
                // Use variant image as swatch background
                const img = document.createElement('img');
                img.src = variantImage.src;
                img.alt = colorValue;
                label.style.backgroundImage = `url(${variantImage.src})`;
                label.style.backgroundSize = 'cover';
                label.style.backgroundPosition = 'center';
              } else {
                // Fallback to color mapping (hanlyn-swatch-color logic)
                const hexColor = this.getColorHex(colorValue);
                label.style.backgroundColor = hexColor;
              }

              // Add click handler to update aria-pressed
              label.addEventListener('click', () => {
                // Remove aria-pressed from all labels in this fieldset
                fieldset.querySelectorAll('.hanlyn-qs__color-swatch').forEach(l => {
                  l.setAttribute('aria-pressed', 'false');
                });
                // Set aria-pressed on clicked label
                label.setAttribute('aria-pressed', 'true');
              });
            }
          });

        } else if (optionName.includes('size')) {
          // Transform to size pills
          fieldset.classList.add('hanlyn-qs__variant-group', 'hanlyn-qs__variant-group--sizes');

          const inputs = fieldset.querySelectorAll('input[type="radio"]');
          inputs.forEach(input => {
            const label = fieldset.querySelector(`label[for="${input.id}"]`);
            if (label) {
              label.classList.add('hanlyn-qs__size-pill');

              // Set pressed state if checked
              if (input.checked) {
                label.setAttribute('aria-pressed', 'true');
              } else {
                label.setAttribute('aria-pressed', 'false');
              }

              // Check if variant is available
              if (input.disabled) {
                label.classList.add('hanlyn-qs__size-pill--unavailable');
                label.setAttribute('aria-disabled', 'true');
              }

              // Add click handler to update aria-pressed
              label.addEventListener('click', () => {
                // Remove aria-pressed from all labels in this fieldset
                fieldset.querySelectorAll('.hanlyn-qs__size-pill').forEach(l => {
                  l.setAttribute('aria-pressed', 'false');
                });
                // Set aria-pressed on clicked label
                label.setAttribute('aria-pressed', 'true');
              });
            }
          });
        }
      });
    }

    /**
     * Get hex color from color name (mirrors hanlyn-swatch-color.liquid logic)
     */
    getColorHex(colorName) {
      const colorLower = colorName.toLowerCase().trim();
      const colorMap = {
        'white': '#ffffff',
        'black': '#111111',
        'navy': '#315270',
        'red': '#cc0000',
        'pink': '#f4a7b9',
        'blue': '#4a90d9',
        'green': '#4a7c59',
        'grey': '#888888',
        'gray': '#888888',
        'beige': '#d4c5a9',
        'cream': '#fdfbf9',
        'brown': '#7c5c3e',
        'purple': '#7c4daa',
        'yellow': '#f5c842',
        'orange': '#e87722',
        'teal': '#297b88',
        'coral': '#e8735a',
        'mint': '#a8d8b9'
      };

      return colorMap[colorLower] || '#cccccc';
    }

    /**
     * Set up Loox reviews integration point
     */
    setupLooxReviews(fullDocument) {
      const reviewsContainer = this.drawer.querySelector('.hanlyn-qs__reviews');
      if (!reviewsContainer) return;

      // Check if Loox reviews exist on the product page
      const looxReviews = fullDocument.querySelector('.loox-rating');
      if (looxReviews) {
        reviewsContainer.innerHTML = looxReviews.outerHTML;
      }
    }

    /**
     * Show the drawer with animation
     */
    showDrawer() {
      this.isOpen = true;

      // Show overlay
      if (this.overlay) {
        this.overlay.classList.add('hanlyn-qs__overlay--active');
      }

      // Lock body scroll
      this.lockScroll();

      // Animate drawer in
      requestAnimationFrame(() => {
        this.drawer.setAttribute('open', '');

        // Set up focus trap after animation
        setTimeout(() => {
          this.setupFocusTrap();
          this.focusFirstElement();
        }, 300); // Match transition duration
      });
    }

    /**
     * Close the drawer
     */
    close() {
      if (!this.isOpen) return;

      this.isOpen = false;

      // Remove focus trap
      this.removeFocusTrap();

      // Animate drawer out
      this.drawer.removeAttribute('open');

      // Hide overlay after animation
      setTimeout(() => {
        if (this.overlay) {
          this.overlay.classList.remove('hanlyn-qs__overlay--active');
        }

        // Unlock body scroll
        this.unlockScroll();

        // Return focus to opener
        if (this.lastFocusedElement) {
          this.lastFocusedElement.focus();
        }
      }, 300); // Match transition duration
    }

    /**
     * Lock body scroll when drawer is open
     */
    lockScroll() {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }

    /**
     * Unlock body scroll when drawer is closed
     */
    unlockScroll() {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }

    /**
     * Set up focus trap within drawer
     */
    setupFocusTrap() {
      const focusableElements = this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      this.focusTrap = Array.from(focusableElements);

      if (this.focusTrap.length > 0) {
        this.drawer.addEventListener('keydown', this.handleFocusTrap);
      }
    }

    /**
     * Remove focus trap
     */
    removeFocusTrap() {
      this.drawer.removeEventListener('keydown', this.handleFocusTrap);
      this.focusTrap = null;
    }

    /**
     * Handle focus trap logic
     */
    handleFocusTrap(event) {
      if (event.key !== 'Tab' || !this.focusTrap || this.focusTrap.length === 0) return;

      const firstElement = this.focusTrap[0];
      const lastElement = this.focusTrap[this.focusTrap.length - 1];

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    }

    /**
     * Focus the first focusable element in the drawer
     */
    focusFirstElement() {
      const closeButton = this.drawer.querySelector('.hanlyn-qs__close');
      if (closeButton) {
        closeButton.focus();
      } else if (this.focusTrap && this.focusTrap.length > 0) {
        this.focusTrap[0].focus();
      }
    }

    // Event Handlers

    handleOverlayClick(event) {
      this.close();
    }

    handleCloseClick(event) {
      event.preventDefault();
      this.close();
    }

    handleEscKey(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    }

    handleThumbnailClick(event) {
      const button = event.currentTarget;
      const index = parseInt(button.getAttribute('data-media-index'), 10);
      this.updateMainImage(index);
    }

    handleArrowClick(event) {
      const button = event.currentTarget;
      const isPrev = button.classList.contains('hanlyn-qs__arrow--prev');

      const thumbnails = this.drawer.querySelectorAll('.hanlyn-qs__thumb');
      let currentIndex = 0;

      thumbnails.forEach((thumb, i) => {
        if (thumb.getAttribute('aria-current') === 'true') {
          currentIndex = i;
        }
      });

      let newIndex;
      if (isPrev) {
        // Cycle to last image if at first image
        newIndex = currentIndex === 0 ? thumbnails.length - 1 : currentIndex - 1;
      } else {
        // Cycle to first image if at last image
        newIndex = currentIndex === thumbnails.length - 1 ? 0 : currentIndex + 1;
      }

      this.updateMainImage(newIndex);
    }

    handleVariantChange(event) {
      // Dawn's variant-selects or variant-radios custom elements handle the logic
      // This is just a hook if we need to respond to variant changes in the drawer
    }

    handleCartUpdate(event) {
      // When an item is added to cart from the quick shop drawer,
      // close the quick shop drawer and let Dawn's cart drawer open automatically
      if (this.isOpen && event.source === 'product-form') {
        this.close();
      }
    }

    handleTouchStart(event) {
      this.touchStartX = event.changedTouches[0].screenX;
      this.touchStartY = event.changedTouches[0].screenY;
    }

    handleTouchEnd(event) {
      this.touchEndX = event.changedTouches[0].screenX;
      this.touchEndY = event.changedTouches[0].screenY;
      this.handleSwipe();
    }

    handleSwipe() {
      const swipeThreshold = 50; // Minimum distance for a swipe
      const horizontalDiff = this.touchEndX - this.touchStartX;
      const verticalDiff = Math.abs(this.touchEndY - this.touchStartY);

      // Only register horizontal swipes (not vertical scrolls)
      if (Math.abs(horizontalDiff) > swipeThreshold && verticalDiff < swipeThreshold) {
        const thumbnails = this.drawer.querySelectorAll('.hanlyn-qs__thumb');
        let currentIndex = 0;

        thumbnails.forEach((thumb, i) => {
          if (thumb.getAttribute('aria-current') === 'true') {
            currentIndex = i;
          }
        });

        let newIndex;
        if (horizontalDiff > 0) {
          // Swipe right - show previous image
          newIndex = currentIndex === 0 ? thumbnails.length - 1 : currentIndex - 1;
        } else {
          // Swipe left - show next image
          newIndex = currentIndex === thumbnails.length - 1 ? 0 : currentIndex + 1;
        }

        this.updateMainImage(newIndex);
      }
    }

    handleZoomClick(event) {
      // Navigate to the full product page to see larger images
      if (this.currentProductUrl) {
        window.location.href = this.currentProductUrl;
      }
    }
  }

  customElements.define('hanlyn-quick-shop-drawer', HanlynQuickShopDrawer);
}
